import * as React from 'react'

const VIEWPORT_PADDING_PX = 8

function getBoundaryRect(container: HTMLElement | null): { left: number; right: number } {
  if (container) {
    const rect = container.getBoundingClientRect()
    return { left: rect.left + VIEWPORT_PADDING_PX, right: rect.right - VIEWPORT_PADDING_PX }
  }

  return {
    left: VIEWPORT_PADDING_PX,
    right: window.innerWidth - VIEWPORT_PADDING_PX,
  }
}

export const useMessageActionButtonsOverflow = (
  isVisible: boolean,
  boundaryRef?: React.RefObject<HTMLElement | null>
) => {
  const actionButtonsRef = React.useRef<HTMLDivElement>(null)
  const [offsetX, setOffsetX] = React.useState(0)

  const updateOffset = React.useCallback(() => {
    const element = actionButtonsRef.current
    if (!element || !isVisible) {
      setOffsetX(0)
      return
    }

    const rect = element.getBoundingClientRect()
    const boundary = getBoundaryRect(boundaryRef?.current ?? null)
    let nextOffset = 0

    if (rect.right > boundary.right) {
      nextOffset = boundary.right - rect.right
    }

    const adjustedLeft = rect.left + nextOffset
    if (adjustedLeft < boundary.left) {
      nextOffset += boundary.left - adjustedLeft
    }

    setOffsetX(nextOffset)
  }, [boundaryRef, isVisible])

  React.useLayoutEffect(() => {
    if (!isVisible) {
      setOffsetX(0)
      return
    }

    updateOffset()

    const frameId = requestAnimationFrame(updateOffset)
    window.addEventListener('resize', updateOffset)
    window.addEventListener('scroll', updateOffset, true)

    const element = actionButtonsRef.current
    const resizeObserver = element ? new ResizeObserver(updateOffset) : null
    if (element && resizeObserver) {
      resizeObserver.observe(element)
    }

    return () => {
      cancelAnimationFrame(frameId)
      window.removeEventListener('resize', updateOffset)
      window.removeEventListener('scroll', updateOffset, true)
      resizeObserver?.disconnect()
    }
  }, [isVisible, updateOffset])

  const overflowStyle = React.useMemo<React.CSSProperties>(
    () => ({
      transform: `translateX(${offsetX}px)`,
      transition: 'transform 150ms ease-out',
    }),
    [offsetX]
  )

  return { actionButtonsRef, overflowStyle }
}
